// THE STANDARD: Login Page (Magic Link Auth)
// Clients enter their email and receive a one-tap login link.
// No redirects to external platforms.

import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import { trpc } from '@/lib/trpc';
import { apiUrl, setNativeSessionToken } from '@/lib/apiBase';
import { STANDARD_LOGO_SRC } from '@/lib/brandAssets';
import { KeyRound, Mail, ShieldCheck } from 'lucide-react';
import '@/styles/light-entry.css';

const BG_URL = 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920&q=80';

type Stage = 'enter_email' | 'sent' | 'error';

const ERROR_MESSAGES: Record<string, string> = {
  missing_token: 'Login link is missing. Please request a new one.',
  token_used: 'This login link has already been used. Request a new one.',
  token_expired: 'This login link has expired. Request a new one below.',
  invalid_token: 'Invalid login link. Request a new one below.',
  db_unavailable: 'Service temporarily unavailable. Please try again.',
  server_error: 'Something went wrong. Please try again.',
};

export default function Login() {
  const [stage, setStage] = useState<Stage>('enter_email');
  const [email, setEmail] = useState(() => localStorage.getItem('the_standard_email') ?? '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [sentEmail, setSentEmail] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [accessMethod, setAccessMethod] = useState<'password' | 'link'>(() => localStorage.getItem('the_standard_email') ? 'password' : 'link');
  // Guideline 1.2: agreement to the Terms of Use (EULA) — including zero
  // tolerance for objectionable content or abusive users — is required before
  // any login or registration request is sent. Remembered after first accept.
  const [agreedToTerms, setAgreedToTerms] = useState(() => localStorage.getItem('the_standard_terms_accepted') === 'yes');
  const [location] = useLocation();
  const hasInvite = Boolean(sessionStorage.getItem('invite_token'));

  const requireTermsAgreement = (): boolean => {
    if (agreedToTerms) return true;
    setErrorMsg('Please agree to the Terms of Use to continue.');
    return false;
  };
  const rememberTermsAccepted = () => localStorage.setItem('the_standard_terms_accepted', 'yes');

  // Forgot-password mutation. Server always returns ok (anti-enumeration), so
  // we display the same confirmation either way: if no account matches, the
  // user simply won't get an email.
  const forgot = trpc.auth.forgotPassword.useMutation({
    onSuccess: () => { setForgotSent(true); setErrorMsg(''); },
    onError: (e) => setErrorMsg(e.message || 'Could not send reset email. Try again.'),
  });

  const handleForgot = (e?: React.FormEvent) => {
    e?.preventDefault();
    setErrorMsg('');
    if (!email.trim() || !email.includes('@')) { setErrorMsg('Enter a valid email first.'); return; }
    forgot.mutate({ email: email.trim().toLowerCase() });
  };
  const isReturningUser = Boolean(localStorage.getItem('the_standard_email'));

  // Tick down the resend cooldown
  useEffect(() => {
    if (resendCooldown <= 0) {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
      return;
    }
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, [resendCooldown]);

  // Read error param from URL (e.g. ?error=token_expired)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get('error');
    if (err && ERROR_MESSAGES[err]) {
      setErrorMsg(ERROR_MESSAGES[err]);
    }
  }, [location]);

  // Password login (alternative to the magic link). Sets the same session
  // cookie server-side, then we hard-navigate so AuthGuard routes by role.
  const loginPw = trpc.auth.loginWithPassword.useMutation({
    onSuccess: (res) => {
      setNativeSessionToken(res.sessionToken);
      localStorage.setItem('the_standard_email', email.trim().toLowerCase());
      const pendingGroupInvite = sessionStorage.getItem('pending_group_invite');
      // A client who arrived via a coach invite link must land back on
      // /invite/:token so the invite redeems (grants coach link + any
      // comped access). Skipping this sent granted clients to the paywall.
      const pendingInviteToken = sessionStorage.getItem('invite_token');
      const isCoachRole = res.role === 'admin' || res.role === 'coach';
      window.location.href =
        pendingGroupInvite
        || (!isCoachRole && pendingInviteToken ? `/invite/${pendingInviteToken}` : null)
        || (isCoachRole ? '/coach/my/today' : '/client');
    },
    onError: (err) => { setErrorMsg(err.message || 'Invalid email or password.'); },
  });

  function handlePasswordLogin(e?: React.FormEvent) {
    e?.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    if (!password) {
      setErrorMsg('Enter your password, or use a login link below.');
      return;
    }
    if (!requireTermsAgreement()) return;
    setErrorMsg('');
    rememberTermsAccepted();
    loginPw.mutate({ email: email.trim().toLowerCase(), password, acceptedTerms: true });
  }

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!email.trim() || !email.includes('@')) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    if (!requireTermsAgreement()) return;
    setLoading(true);
    setErrorMsg('');
    rememberTermsAccepted();

    // Carry invite token if present in sessionStorage
    const inviteToken = sessionStorage.getItem('invite_token') ?? undefined;
    const redirectTo = sessionStorage.getItem('pending_group_invite') ?? undefined;

    try {
      const res = await fetch(apiUrl('/api/auth/magic-link'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), inviteToken, redirectTo, acceptedTerms: true }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setErrorMsg(data.error ?? 'Failed to send login link. Please try again.');
        setLoading(false);
        return;
      }
      setSentEmail(email.trim().toLowerCase());
      // Remember email for next visit so they don't have to re-type it
      localStorage.setItem('the_standard_email', email.trim().toLowerCase());
      setStage('sent');
      setResendCooldown(60);
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }

  // Detect the email provider to show a direct "Open" link
  function getEmailProvider(addr: string): { name: string; url: string; color: string } | null {
    const domain = addr.split('@')[1]?.toLowerCase();
    if (!domain) return null;
    if (domain === 'gmail.com' || domain === 'googlemail.com') return { name: 'Gmail', url: 'https://mail.google.com', color: '#EA4335' };
    if (domain === 'yahoo.com' || domain === 'ymail.com') return { name: 'Yahoo Mail', url: 'https://mail.yahoo.com', color: '#6001D2' };
    if (domain === 'outlook.com' || domain === 'hotmail.com' || domain === 'live.com' || domain === 'msn.com') return { name: 'Outlook', url: 'https://outlook.live.com', color: '#0078D4' };
    if (domain === 'icloud.com' || domain === 'me.com' || domain === 'mac.com') return { name: 'iCloud Mail', url: 'https://www.icloud.com/mail', color: '#3693F3' };
    if (domain === 'aol.com') return { name: 'AOL Mail', url: 'https://mail.aol.com', color: '#31459B' };
    if (domain === 'protonmail.com' || domain === 'proton.me' || domain === 'pm.me') return { name: 'Proton Mail', url: 'https://mail.proton.me', color: '#6D4AFF' };
    return null;
  }

  async function handleResend() {
    if (resendCooldown > 0 || !sentEmail) return;
    setLoading(true);
    try {
      const inviteToken = sessionStorage.getItem('invite_token') ?? undefined;
      const redirectTo = sessionStorage.getItem('pending_group_invite') ?? undefined;
      const res = await fetch(apiUrl('/api/auth/magic-link'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: sentEmail, inviteToken, redirectTo, acceptedTerms: true }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setErrorMsg(data.error ?? 'Failed to resend. Try again.');
        setStage('enter_email');
        return;
      }
      setResendCooldown(60);
    } catch {
      setErrorMsg('Network error.');
      setStage('enter_email');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="le-shell le-center">
      <div className="le-frame le-stack">
        {/* Brand plate: the one dark accent, carrying the gold and white logo. */}
        <div className="le-brand-plate" data-photo="" style={{ backgroundImage: `url(${BG_URL})` }}>
          <img src={STANDARD_LOGO_SRC} alt="The Standard" className="le-brand-logo" />
        </div>

        {/* Card */}
        <div className="le-card le-card-pad">

          {stage === 'enter_email' && (
            <>
              <p className="le-eyebrow le-center-text">
                {isReturningUser ? 'Welcome Back' : 'Member Access'}
              </p>
              <h1 className="le-title le-center-text mt-2">
                Enter The Standard
              </h1>
              <p className="le-copy le-center-text mt-3 mb-5">
                First visit? Request a login link using the email you used to enroll. No password needed.
              </p>

              {hasInvite && (
                <div className="le-alert le-alert-note mb-4">
                  Your coaching invitation is ready. Sign in and the system will bring you back to finish connecting with your coach.
                </div>
              )}

              {errorMsg && (
                <div role="alert" className="le-alert le-alert-error mb-4">
                  {errorMsg}
                </div>
              )}

              <div className="le-switch mb-4" aria-label="Choose a login method">
                <button
                  type="button"
                  aria-pressed={accessMethod === 'password'}
                  onClick={() => { setAccessMethod('password'); setErrorMsg(''); }}
                >
                  <span className="inline-flex items-center justify-center gap-2"><KeyRound className="w-4 h-4" /> Password</span>
                </button>
                <button
                  type="button"
                  aria-pressed={accessMethod === 'link'}
                  onClick={() => { setAccessMethod('link'); setForgotMode(false); setErrorMsg(''); }}
                >
                  <span className="inline-flex items-center justify-center gap-2"><Mail className="w-4 h-4" /> Login Link</span>
                </button>
              </div>

              <form onSubmit={accessMethod === 'password' ? handlePasswordLogin : handleSubmit} className="space-y-3">
                <input
                  aria-label="Email address"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  autoComplete="email"
                  className="le-input"
                />
                {accessMethod === 'password' && (
                  <input
                    aria-label="Password"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Password"
                    autoComplete="current-password"
                    className="le-input"
                  />
                )}
                {/* EULA / Terms agreement: required before login or registration */}
                <label
                  data-terms-agreement
                  className="le-check select-none"
                >
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => { setAgreedToTerms(e.target.checked); if (e.target.checked) setErrorMsg(''); }}
                    aria-label="Agree to the Terms of Use"
                  />
                  <span>
                    I agree to the{' '}
                    <Link href="/terms" className="le-link">
                      Terms of Use (EULA)
                    </Link>
                    . There is no tolerance for objectionable content or abusive users. Offending
                    content is removed and accounts that post it are terminated.
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={loginPw.isPending || loading || !agreedToTerms}
                  className="le-btn le-btn-primary"
                >
                  {accessMethod === 'password'
                    ? loginPw.isPending ? 'Logging in...' : 'Log In'
                    : loading ? 'Sending...' : 'Send Secure Login Link'}
                </button>

                <p className="le-small le-center-text">
                  {accessMethod === 'password'
                    ? 'Use Login Link if you have not set a password yet.'
                    : 'The link expires in 15 minutes and can only be used once.'}
                </p>

                {/* Forgot password: collapsible to keep the primary path uncluttered */}
                {accessMethod === 'password' && !forgotMode && (
                  <button
                    type="button"
                    onClick={() => { setForgotMode(true); setErrorMsg(''); setForgotSent(false); }}
                    className="le-text-btn le-text-btn-block"
                  >
                    Forgot password?
                  </button>
                )}

                {forgotMode && !forgotSent && (
                  <div className="mt-2 pt-3 space-y-2 border-t border-[#d9d4ca]">
                    <p className="le-small le-center-text">
                      Enter your email above, then tap below to get a reset link.
                    </p>
                    <button
                      type="button"
                      onClick={handleForgot}
                      disabled={forgot.isPending}
                      className="le-btn le-btn-secondary"
                    >
                      {forgot.isPending ? 'Sending...' : 'Send Reset Link'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setForgotMode(false)}
                      className="le-text-btn le-text-btn-block"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {forgotMode && forgotSent && (
                  <div className="mt-2 pt-3 text-center border-t border-[#d9d4ca]">
                    <p className="le-alert le-alert-success" role="status">
                      If that email is on file, a reset link is on the way.
                    </p>
                    <p className="le-small mt-2">
                      Check spam if it doesn't arrive within a few minutes.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setForgotMode(false); setForgotSent(false); }}
                      className="le-text-btn mt-2"
                    >
                      Back to login
                    </button>
                  </div>
                )}
              </form>

              <div className="le-alert le-alert-steel mt-4 flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#2f5d73' }} />
                <div>
                  <strong className="block font-bold">Secure member access</strong>
                  <span className="le-small block mt-0.5">Enrolled in The Standard or invited by your coach? Use that same email to sign in.</span>
                </div>
              </div>

            </>
          )}

          {stage === 'sent' && (() => {
            const provider = getEmailProvider(sentEmail);
            return (
            <div className="text-center space-y-5 py-2">
              {/* Icon */}
              <div className="le-icon-badge mx-auto" data-tone="steel">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>

              {/* Heading */}
              <div>
                <p className="le-eyebrow mb-1">
                  Check Your Inbox
                </p>
                <h2 className="le-title le-title-sm">
                  Link Sent
                </h2>
              </div>

              <p className="le-copy">
                We sent a login link to <strong className="le-strong break-all">{sentEmail}</strong>.
              </p>

              {/* Open Email: big, obvious CTA */}
              {provider ? (
                <a
                  href={provider.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="le-btn le-btn-steel"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                    <polyline points="15,3 21,3 21,9"/>
                    <line x1="10" y1="14" x2="21" y2="3"/>
                  </svg>
                  Open {provider.name}
                </a>
              ) : (
                <p className="le-small">
                  Open your email app and look for the message from The Standard.
                </p>
              )}

              {/* Step-by-step guide */}
              <div className="le-inset text-left space-y-2">
                {[
                  'Open your email app',
                  'Find the email from The Standard',
                  'Tap the login link inside',
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="le-step-num">
                      {i + 1}
                    </span>
                    <span className="le-small" style={{ color: '#17151a' }}>{step}</span>
                  </div>
                ))}
              </div>

              {/* Spam hint */}
              <p className="le-small">
                Don't see it? Check your <strong className="le-strong">spam</strong> or <strong className="le-strong">promotions</strong> folder.
              </p>

              {/* Resend + different email */}
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || loading}
                  className="le-btn le-btn-secondary flex-1"
                >
                  {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend Link'}
                </button>
                <button
                  onClick={() => { setStage('enter_email'); setErrorMsg(''); }}
                  className="le-btn le-btn-secondary flex-1"
                >
                  Different Email
                </button>
              </div>

              {/* Link expiry note */}
              <p className="le-small">
                Link expires in 15 minutes.
              </p>
            </div>
            );
          })()}
        </div>

        <p className="le-footer-note">
          The Standard by Team FGP.
        </p>
      </div>
    </div>
  );
}
